import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { ITcProductComponentInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { deriveProductTotals } from "./component-payload";
import { formatTcV4Decimal } from "../../../../tc-v4-decimals";

interface ProductTotalsSummaryProps {
  components: ITcProductComponentInformation[];
  productSupplementaryWeight?: number;
}

/**
 * Read-only "Product Total Derived From Components" summary, shown above the derived product
 * Raw Material table in the WITH-component flow (Net Shipping = Σ component net + supplementary,
 * Certified = Σ component certified, Non-Certified = Σ component non-certified).
 */
const ProductTotalsSummary: React.FC<ProductTotalsSummaryProps> = ({
  components,
  productSupplementaryWeight = 0,
}) => {
  const totals = useMemo(
    () => deriveProductTotals(components || [], productSupplementaryWeight),
    [components, productSupplementaryWeight]
  );

  return (
    <Box mb={2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Product Total Derived From Components
      </Typography>
      {/* Vertical Weight Type / Weight table. At the product stage the full product breakdown is
          shown: Net Shipping = Σ component net + supplementary; the Certified + Non-Certified
          (material) weight = Σ component net. Matches the wireframe's 5-row derived-totals table. */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 440 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#2C4748" }}>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Weight Type</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="right">
                Weight (kg)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Net Shipping Wt</TableCell>
              <TableCell align="right">{formatTcV4Decimal(totals.productNetShippingWeight)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Supplementary Wt</TableCell>
              <TableCell align="right">
                {formatTcV4Decimal(productSupplementaryWeight || 0)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Certified + Non-Certified Wt</TableCell>
              <TableCell align="right">{formatTcV4Decimal(totals.componentNetSum)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Certified Wt</TableCell>
              <TableCell align="right">{formatTcV4Decimal(totals.productCertifiedWeight)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Non-Certified Wt</TableCell>
              <TableCell align="right">{formatTcV4Decimal(totals.productNonCertifiedWeight)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductTotalsSummary;
